import { createApp } from "vue"
import { createPinia } from "pinia"

// Self-hosted variable fonts (replaces fonts.googleapis.com link in
// index.html — keeps CSP strict at style-src 'self'). The explicit
// /index.css suffix lets vue-tsc resolve via vite/client's *.css
// module declaration without an extra ambient .d.ts.
import "@fontsource-variable/inter/index.css"
import "@fontsource-variable/inter-tight/index.css"
import "@fontsource-variable/jetbrains-mono/index.css"

import App from "./App.vue"
import router from "./router"
import "./style.css"

import { refresh as refreshAuth } from "@/api/auth"
import { installAuthInterceptor } from "@/api/client"
import { getMe } from "@/api/me"
import { useAuthStore } from "@/stores/auth"
import { useThemeStore } from "@/stores/theme"
import { useToastStore } from "@/stores/toast"

async function bootstrap(): Promise<void> {
  const app = createApp(App)
  const pinia = createPinia()
  app.use(pinia)

  // Touch the theme store so it applies the right class on <html> before the
  // first paint (avoids a light→dark or dark→light flash on cold load).
  useThemeStore()

  // Install the auth interceptor BEFORE the cold-start getMe call so the
  // bearer token header lands on it. Without this, getMe receives no
  // Authorization header, returns 401, bootstrap swallows the error, and we
  // end up with accessToken set but user=null — which the router guard reads
  // as "authenticated but not admin" and silently redirects /admin/... to /.
  const toasts = useToastStore()
  installAuthInterceptor({
    onRefreshFailed: () => {
      toasts.warning("Session expired — please log in again")
      router.push("/login")
    },
  })

  // Cold-start auth hydration: try the refresh cookie before the router
  // mounts. If it works, also fetch /me so the auth store has the user.
  // On any failure: stay logged out (the router guard will redirect to /login).
  const auth = useAuthStore()
  try {
    const res = await refreshAuth()
    auth.setAccessToken(res.access_token)
    const me = await getMe()
    auth.setSession(res.access_token, me)
  } catch {
    // No valid session — fine, the guard will redirect.
  }

  app.use(router)
  app.mount("#app")
}

bootstrap()
